import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowRight,
  FaChartLine,
  FaCheckCircle,
  FaCreditCard,
  FaEnvelope,
  FaExclamationCircle,
  FaGasPump,
  FaInfoCircle,
  FaLock,
  FaOilCan,
  FaPhoneAlt,
  FaReceipt,
  FaShieldAlt,
  FaTimes,
  FaUserAlt,
  FaUsersCog,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./CSS/login.css";
import logo from "./CSS/fuelFlowLogo.png";

const initialDemoForm = {
  company_name: "",
  contact_person: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  message: "",
};

const highlights = [
  "Daily shift reconciliation",
  "Owner level profit visibility",
  "Operator friendly mobile screens",
];

const metrics = [
  ["24/7", "station visibility"],
  ["1 flow", "readings to reports"],
  ["Multi", "bunk ready"],
];

const livePreviewRows = [
  ["Nozzle A1", "1,284 L", "Synced"],
  ["Shift Cash", "Rs 42,850", "Matched"],
  ["Credit Due", "Rs 8,400", "Tracked"],
];

const features = [
  {
    icon: FaGasPump,
    title: "Nozzle Readings",
    text: "Opening and closing readings with shift mapping, pump-wise control, and accurate volume calculations.",
  },
  {
    icon: FaReceipt,
    title: "Reconciliation",
    text: "Cash, UPI, card, HP Pay, credits, and variance tracking in one clean daily workflow.",
  },
  {
    icon: FaChartLine,
    title: "P&L & Margins",
    text: "Fuel margin, lube performance, expenses, and operational profit visible without manual chasing.",
  },
  {
    icon: FaUsersCog,
    title: "Employees",
    text: "Operator details, shifts, salary, proofs, and salary-linked credit deductions stay organized.",
  },
  {
    icon: FaCreditCard,
    title: "Credits",
    text: "Customer and employee credits with pending balances, settlement tracking, and filters.",
  },
  {
    icon: FaOilCan,
    title: "Fuel Stock",
    text: "Track stock, reduce leakage risk, improve audit readiness, and keep tighter control.",
  },
];

const modules = [
  ["Operations", "Pumps, nozzles, readings, shifts, operator workflow, and daily control."],
  ["Finance", "Reconciliation, expenses, credits, P&L, and export-ready reporting."],
  ["Management", "Users, roles, support workflows, tenant setup, and platform controls."],
  ["Automation", "WhatsApp notifications, demo intake, onboarding, and future AI workflows."],
];

const demoFields = [
  ["company_name", "Company / Fuel Station Name", "Enter company or bunk name", true],
  ["contact_person", "Contact Person", "Enter contact person", true],
  ["phone", "Phone Number", "Enter phone number", true],
  ["email", "Email Address", "Enter email address", false],
];

export default function Login() {
  const navigate = useNavigate();
  const loginRef = useRef(null);
  const demoRef = useRef(null);

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [demoForm, setDemoForm] = useState(initialDemoForm);
  const [submittingDemo, setSubmittingDemo] = useState(false);
  const [loginNotice, setLoginNotice] = useState(null);
  const [demoNotice, setDemoNotice] = useState(null);

  const loginDisabled = useMemo(
    () => !phone.trim() || !password || loggingIn,
    [phone, password, loggingIn]
  );

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!loginNotice) return undefined;

    const timer = setTimeout(() => {
      setLoginNotice(null);
    }, loginNotice.type === "error" ? 5600 : 4400);

    return () => clearTimeout(timer);
  }, [loginNotice]);

  useEffect(() => {
    if (!demoNotice) return undefined;

    const timer = setTimeout(() => {
      setDemoNotice(null);
    }, demoNotice.type === "error" ? 5600 : 4400);

    return () => clearTimeout(timer);
  }, [demoNotice]);

  const showNotice = (setter, type, title, message) => {
    setter({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  const updateDemoField = (key, value) => {
    setDemoForm((prev) => ({ ...prev, [key]: value }));
  };

  const routeAfterLogin = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    if (normalizedRole === "super_admin" || normalizedRole === "support_admin") {
      navigate("/platform");
    } else if (normalizedRole === "admin" || normalizedRole === "manager") {
      navigate("/admin");
    } else if (normalizedRole === "operator") {
      navigate("/operator");
    } else if (normalizedRole === "customer") {
      navigate("/customer");
    } else {
      navigate("/");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (loginDisabled) return;

    try {
      setLoggingIn(true);
      const { data } = await api.post("/api/auth/login", {
        phone: phone.trim(),
        password,
      });

      const { token, user } = data;

      localStorage.setItem("token", token);
      localStorage.setItem("role", user.role);
      localStorage.setItem("userId", JSON.stringify(user.id));
      localStorage.setItem("bunkname", JSON.stringify(user.bunkname || ""));
      localStorage.setItem("name", JSON.stringify(user.name || ""));
      localStorage.setItem("tenantId", JSON.stringify(user.tenant_id || null));
      localStorage.setItem("isSuperAdmin", JSON.stringify(user.is_super_admin || 0));
      localStorage.setItem("company", JSON.stringify(user.company || ""));
      localStorage.setItem("company_name", JSON.stringify(user.company || ""));
      localStorage.setItem("bunk_address", JSON.stringify(user.bunk_address || ""));
      localStorage.setItem("admin_phone", JSON.stringify(user.admin_phone || user.phone || ""));
      localStorage.setItem("phone", JSON.stringify(user.phone || ""));

      routeAfterLogin(user.role);
    } catch (err) {
      showNotice(
        setLoginNotice,
        "error",
        "Login failed",
        err?.response?.data?.error || "Please check your phone number and password."
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const submitDemoRequest = async (e) => {
    e.preventDefault();

    if (!demoForm.company_name || !demoForm.contact_person || !demoForm.phone) {
      showNotice(
        setDemoNotice,
        "error",
        "Missing details",
        "Company name, contact person, and phone are required."
      );
      return;
    }

    try {
      setSubmittingDemo(true);

      await api.post("/api/demo-requests", {
        company_name: demoForm.company_name.trim(),
        contact_person: demoForm.contact_person.trim(),
        phone: demoForm.phone.trim(),
        email: demoForm.email.trim() || null,
        city: demoForm.city.trim() || null,
        state: demoForm.state.trim() || null,
        message: demoForm.message.trim() || null,
      });

      showNotice(
        setDemoNotice,
        "success",
        "Demo request sent",
        "Our team will contact you soon with the next steps."
      );
      setDemoForm(initialDemoForm);
    } catch (err) {
      showNotice(
        setDemoNotice,
        "error",
        "Request not submitted",
        err?.response?.data?.error || "Something went wrong while submitting your demo request."
      );
    } finally {
      setSubmittingDemo(false);
    }
  };

  return (
    <div className="ff-site ff-login-page">
      <div className="ff-bg-grid" />

      <header className="ff-nav">
        <div className="ff-container ff-nav-inner">
          <button
            className="ff-brand"
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Go to top"
          >
            <img className="ff-logo" src={logo} alt="FuelFlow" />
            <span>
              <span className="ff-brand-name">FuelFlow</span>
              <span className="ff-brand-sub">Petrol bunk operations, simplified</span>
            </span>
          </button>

          <nav className="ff-links" aria-label="Primary">
            <a href="#features">Features</a>
            <a href="#modules">Modules</a>
            <a href="#demo">Get Demo</a>
          </nav>

          <div className="ff-actions">
            <button className="ff-btn ghost" type="button" onClick={() => scrollTo(loginRef)}>
              Login
            </button>
            <button className="ff-btn primary" type="button" onClick={() => scrollTo(demoRef)}>
              Request Demo
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="ff-login-hero">
          <div className="ff-container ff-login-hero-grid">
            <div className="ff-hero-copy">
              <div className="ff-kicker">Built for fuel stations in India</div>
              <h1 className="ff-hero-title">
                Run readings, reconciliation, credits, stock, and profit from one place.
              </h1>
              <p className="ff-hero-text">
                FuelFlow keeps daily petrol bunk operations simple for owners, managers,
                support teams, and operators without forcing them through complicated screens.
              </p>

              <div className="ff-hero-actions">
                <button className="ff-btn primary big" type="button" onClick={() => scrollTo(loginRef)}>
                  Login Now <FaArrowRight aria-hidden="true" />
                </button>
                <button className="ff-btn ghost big" type="button" onClick={() => scrollTo(demoRef)}>
                  Book a Demo
                </button>
              </div>

              <div className="ff-highlight-list">
                {highlights.map((item) => (
                  <span key={item}>
                    <FaCheckCircle aria-hidden="true" />
                    {item}
                  </span>
                ))}
              </div>

              <OperationPreview />
            </div>

            <div className="ff-login-panel-wrap" ref={loginRef}>
              <NotificationToast notice={loginNotice} onClose={() => setLoginNotice(null)} />
              <LoginPanel
                loggingIn={loggingIn}
                loginDisabled={loginDisabled}
                password={password}
                phone={phone}
                showPassword={showPassword}
                onLogin={login}
                onPasswordChange={setPassword}
                onPhoneChange={setPhone}
                onShowPasswordChange={setShowPassword}
                onDemoClick={() => scrollTo(demoRef)}
              />
            </div>
          </div>
        </section>

        <section className="ff-metrics-band">
          <div className="ff-container ff-metrics-grid">
            {metrics.map(([value, label]) => (
              <div className="ff-metric" key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="features" className="ff-section">
          <div className="ff-container">
            <div className="ff-section-head">
              <h2 className="ff-h2">Everything needed for daily control</h2>
              <p className="ff-sub">
                Practical workflows that match how fuel stations actually work shift by shift.
              </p>
            </div>

            <div className="ff-grid3">
              {features.map(({ icon: Icon, title, text }) => (
                <div className="ff-card" key={title}>
                  <div className="ff-ic">
                    <Icon aria-hidden="true" />
                  </div>
                  <div className="ff-card-title">{title}</div>
                  <div className="ff-card-text">{text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="modules" className="ff-section alt">
          <div className="ff-container">
            <div className="ff-section-head compact">
              <h2 className="ff-h2">Clear modules, faster onboarding</h2>
              <p className="ff-sub">
                Start with the basics and enable more workflows as your station grows.
              </p>
            </div>

            <div className="ff-module-list">
              {modules.map(([title, text], index) => (
                <div className="ff-module-row" key={title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="demo" className="ff-section ff-demo-section" ref={demoRef}>
          <div className="ff-container ff-demo-grid">
            <div>
              <div className="ff-kicker">Live walkthrough</div>
              <h2 className="ff-h2">See FuelFlow with your station workflow in mind.</h2>
              <p className="ff-sub">
                Share a few details and we will show how readings, reconciliation, credits,
                stock, and reporting can fit your day-to-day process.
              </p>

              <div className="ff-demo-help">
                <div>
                  <FaPhoneAlt aria-hidden="true" />
                  <span>Phone: +91-XXXXXXXXXX</span>
                </div>
                <div>
                  <FaEnvelope aria-hidden="true" />
                  <span>Email: support@fuelflow.in</span>
                </div>
              </div>
            </div>

            <div className="ff-contact-card">
              <NotificationToast
                notice={demoNotice}
                onClose={() => setDemoNotice(null)}
                className="embedded"
              />
              <h3 className="ff-h3">Request Demo</h3>
              <DemoForm
                demoForm={demoForm}
                submittingDemo={submittingDemo}
                onSubmit={submitDemoRequest}
                onUpdate={updateDemoField}
              />
            </div>
          </div>
        </section>
      </main>

      <footer className="ff-footer">
        <div className="ff-container ff-footer-inner">
          <div>&copy; {new Date().getFullYear()} FuelFlow.in</div>
          <div className="ff-footer-links">
            <a href="#features">Features</a>
            <a href="#demo">Get Demo</a>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <button className="ff-linkbtn" type="button" onClick={() => scrollTo(loginRef)}>
              Login
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LoginPanel({
  loggingIn,
  loginDisabled,
  password,
  phone,
  showPassword,
  onDemoClick,
  onLogin,
  onPasswordChange,
  onPhoneChange,
  onShowPasswordChange,
}) {
  return (
    <div className="ff-login-panel">
      <div className="ff-login-panel-head">
        <img src={logo} alt="FuelFlow" />
        <div>
          <h2>Welcome back</h2>
          <p>Login to your station dashboard</p>
        </div>
      </div>

      <div className="ff-session-strip" aria-hidden="true">
        <span />
        <strong>Secure session ready</strong>
        <em>Role access</em>
      </div>

      <form className="ff-login-form" onSubmit={onLogin}>
        <label className="ff-control">
          <span>Phone Number</span>
          <div className="ff-input-icon">
            <FaUserAlt aria-hidden="true" />
            <input
              autoComplete="tel"
              inputMode="tel"
              placeholder="Enter your phone number"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
            />
          </div>
        </label>

        <label className="ff-control">
          <span>Password</span>
          <div className="ff-input-icon">
            <FaLock aria-hidden="true" />
            <input
              autoComplete="current-password"
              placeholder="Enter your password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
            />
            <button
              className="ff-password-toggle"
              type="button"
              onClick={() => onShowPasswordChange((value) => !value)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <button className="ff-btn primary w100 ff-login-submit" type="submit" disabled={loginDisabled}>
          {loggingIn ? "Logging in..." : "Login to FuelFlow"}
        </button>
      </form>

      <div className="ff-secure-note">
        <FaShieldAlt aria-hidden="true" />
        <span>Role based access for platform, admin, manager, and operator users.</span>
      </div>

      <div className="ff-login-panel-foot">
        New station?
        <button type="button" onClick={onDemoClick}>
          Request demo
        </button>
      </div>
    </div>
  );
}

function NotificationToast({ notice, onClose, className = "" }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error"
        ? FaExclamationCircle
        : FaInfoCircle;

  return (
    <div
      className={`ff-toast ${notice.type} ${className}`.trim()}
      role={notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "error" ? "assertive" : "polite"}
      key={notice.id}
    >
      <div className="ff-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="ff-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="ff-toast-close" type="button" onClick={onClose} aria-label="Close message">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function OperationPreview() {
  return (
    <div className="ff-live-preview" aria-hidden="true">
      <div className="ff-live-preview-head">
        <span>Today</span>
        <strong>Live station flow</strong>
      </div>
      <div className="ff-flow-line">
        <span />
        <span />
        <span />
      </div>
      <div className="ff-preview-rows">
        {livePreviewRows.map(([label, value, status]) => (
          <div className="ff-preview-row" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{status}</em>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoForm({ demoForm, submittingDemo, onSubmit, onUpdate }) {
  return (
    <form onSubmit={onSubmit}>
      {demoFields.map(([key, label, placeholder, required]) => (
        <label className="ff-control" key={key}>
          <span>{label}</span>
          <input
            className="ff-input"
            inputMode={key === "phone" ? "tel" : undefined}
            placeholder={placeholder}
            required={required}
            type={key === "email" ? "email" : "text"}
            value={demoForm[key]}
            onChange={(e) => onUpdate(key, e.target.value)}
          />
        </label>
      ))}

      <div className="ff-inline-grid">
        <label className="ff-control">
          <span>City / Location</span>
          <input
            className="ff-input"
            placeholder="City"
            value={demoForm.city}
            onChange={(e) => onUpdate("city", e.target.value)}
          />
        </label>
        <label className="ff-control">
          <span>State</span>
          <input
            className="ff-input"
            placeholder="State"
            value={demoForm.state}
            onChange={(e) => onUpdate("state", e.target.value)}
          />
        </label>
      </div>

      <label className="ff-control">
        <span>Message</span>
        <textarea
          className="ff-input ff-textarea"
          placeholder="Tell us what you want to see in the demo"
          value={demoForm.message}
          onChange={(e) => onUpdate("message", e.target.value)}
        />
      </label>

      <button className="ff-btn primary w100" type="submit" disabled={submittingDemo}>
        {submittingDemo ? "Submitting..." : "Submit Demo Request"}
      </button>
    </form>
  );
}
